/**
 * Decorative backdrop for the "Zero-Day & Exploit Production House" banner.
 *
 * Two arrangements, because Figma draws two: node 2601:58404 for the 1440 × 825
 * desktop banner and node 2612:38875 for the 390 × 844 phone banner (the blocks
 * are re-scattered there, not just scaled).
 *
 * Both are stretched to the hero (`preserveAspectRatio="none"`) so the whole
 * composition compresses instead of being cropped — the hero is proportionally
 * far taller than either design frame, and `slice` would hide the right half.
 *
 * Figma's "+" cross marks (a triple at the top right and another at the bottom
 * left on desktop, one triple on the phone layout) are deliberately left out —
 * dropped on request. Everything else is a plain block, so nothing else needs to
 * escape the stretch to keep its shape.
 *
 * Coordinates come from `get_design_context`, not `get_metadata`: Figma rotates
 * several of these rects, and metadata reports a rotated node's *bottom* edge as
 * its `y`, which drops every one of them by its own height.
 */

type Rect = readonly [number, number, number, number];

const DESKTOP_BLOCKS: readonly Rect[] = [
  [81, 128, 40, 40],
  [321, 128, 40, 40],
  [385, 128, 40, 40],
  [464, 128, 221, 40],
  [788, 127, 221, 40],
  [424, 168, 40, 200],
  [685, 168, 40, 200],
  [748, 167, 40, 200],
  [1009, 167, 40, 200],
  [81, 368, 40, 40],
  [321, 368, 40, 40],
  [385, 368, 40, 40],
  [464, 368, 221, 40],
  [788, 367, 221, 40],
  /* The four blocks Figma used to hang off the headline — a bar and a cluster
     of three. They stayed put at y 489–671 when the design moved the headline to
     the top of the frame (y=207), so they read as part of the scatter now rather
     than as aligned to the copy, and they belong on the stretched frame with
     everything else instead of tracking the text. */
  [70, 489, 388, 49],
  [955, 594, 388, 77],
  [955, 546, 40, 40],
  [897, 631, 40, 40],
];

/**
 * Primary/600 accents — desktop only. The 10 × 38 bar at (1343, 556) used to be
 * dropped, because the one line headline sat at y=517 and the bar landed on the
 * last glyph of "House" around 1440. The headline is at the top of the frame
 * now, so nothing collides and the bar is back.
 */
const DESKTOP_ACCENTS: readonly Rect[] = [
  [117, 158, 8, 8],
  [1305, 149, 10, 10],
  [1343, 556, 10, 38],
];

const MOBILE_BLOCKS: readonly Rect[] = [
  [60.59, 93.49, 19.696, 19.696],
  [92.11, 93.49, 19.696, 19.696],
  [131.01, 93.49, 108.822, 19.696],
  [290.55, 93, 108.822, 19.696],
  [111.31, 113.19, 19.696, 98.481],
  [239.83, 113.19, 19.696, 98.481],
  [270.85, 112.7, 19.696, 98.481],
  [60.59, 211.67, 19.696, 19.696],
  [92.11, 211.67, 19.696, 19.696],
  [131.01, 211.67, 108.822, 19.696],
  [290.55, 211.18, 108.822, 19.696],
  [-63, 271.25, 191.054, 24.128],
  [170.72, 585.69, 19.696, 19.696],
  [170.72, 609.32, 191.054, 37.915],
  [134, 669.77, 21.068, 21.068],
];

function Blocks({ blocks, accents = [] }: { blocks: readonly Rect[]; accents?: readonly Rect[] }) {
  return (
    <>
      {blocks.map(([x, y, w, h]) => (
        <rect
          key={`b-${x}-${y}-${w}`}
          x={x}
          y={y}
          width={w}
          height={h}
          fill="#1f2428"
          fillOpacity="0.5"
        />
      ))}
      {accents.map(([x, y, w, h]) => (
        <rect key={`a-${x}-${y}`} x={x} y={y} width={w} height={h} fill="#ec1938" />
      ))}
    </>
  );
}

export default function ProductionHouseBackdrop() {
  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
      {/* ── lg and up ─────────────────────────────────────────────────── */}
      <svg
        className="absolute inset-0 hidden h-full w-full lg:block"
        viewBox="0 0 1440 825"
        preserveAspectRatio="none"
      >
        {/* 200×200 block with a 39px wall — a stroked rect inset by half the wall. */}
        <rect
          x="140.5"
          y="187.5"
          width="161"
          height="161"
          fill="none"
          stroke="#1f2428"
          strokeOpacity="0.5"
          strokeWidth="39"
        />
        <Blocks blocks={DESKTOP_BLOCKS} accents={DESKTOP_ACCENTS} />
      </svg>
      {/* ── below lg ──────────────────────────────────────────────────── */}
      <svg
        className="absolute inset-0 h-full w-full lg:hidden"
        viewBox="0 0 390 844"
        preserveAspectRatio="none"
      >
        {/* Same block, scaled to 98.481 but keeping Figma's un-scaled 39px wall. */}
        <rect
          x="-18.39"
          y="132.69"
          width="59.481"
          height="59.481"
          fill="none"
          stroke="#1f2428"
          strokeOpacity="0.5"
          strokeWidth="39"
        />
        <Blocks blocks={MOBILE_BLOCKS} />
      </svg>
    </div>
  );
}
